interface FormCategory {
  categoryName: string;
  fields: FormField[];
}

interface FormField {
  blockEdit?: boolean;
  objectKey: string;
  langKey?: string;
  fieldType?: 'text' | 'date' | 'select' | 'textarea';
  optionsBuilder?: () => { value: string; label: string }[];
}

export interface FormParams {
  countryOptionBuilder: () => { value: string; label: string }[];
  showIssues: boolean;
  showEmployeeRemarks: boolean;
  showAgencyRemarks: boolean;
  showPoolFields: boolean;
}

export const getForm = ({
  countryOptionBuilder,
  showIssues,
  showEmployeeRemarks,
  showAgencyRemarks,
  showPoolFields
}: FormParams): FormCategory[] => {
  const categories: FormCategory[] = [
    getBaseCategory(showPoolFields),
    getContactCategory(),
    getAddressCategory(countryOptionBuilder),
    getAdditionalInfoCategory(
      showEmployeeRemarks,
      showAgencyRemarks,
      showIssues
    )
  ];

  return categories;
};

const getBaseCategory = (showPoolFields: boolean): FormCategory => {
  const category: FormCategory = {
    categoryName: 'base',
    fields: [
      {
        objectKey: 'code',
        blockEdit: true
      },
      {
        objectKey: 'name',
        blockEdit: true
      },
      {
        objectKey: 'nameShort',
        blockEdit: true
      },
      {
        objectKey: 'brType',
        langKey: 'type',
        blockEdit: true
      },
      {
        objectKey: 'validFrom',
        fieldType: 'date',
        blockEdit: true
      },
      {
        objectKey: 'validTo',
        fieldType: 'date',
        blockEdit: true
      },
      ...(showPoolFields
        ? [
            {
              blockEdit: true,
              objectKey: 'requestPool'
            },
            {
              blockEdit: true,
              objectKey: 'employeePool'
            }
          ]
        : [])
    ]
  };

  return category;
};

const getContactCategory = (): FormCategory => {
  const category: FormCategory = {
    categoryName: 'contact',
    fields: [
      {
        objectKey: 'budgetKeeper'
      },
      {
        objectKey: 'manager',
        langKey: 'executive'
      },
      {
        objectKey: 'comEmail',
        langKey: 'email'
      },
      {
        objectKey: 'comEmailCc',
        langKey: 'emailCC'
      },
      {
        objectKey: 'privilegedEmailAddress'
      },
      {
        objectKey: 'comPhone',
        langKey: 'phone'
      },
      {
        objectKey: 'comSms',
        langKey: 'sms'
      }
    ]
  };

  return category;
};

const getAddressCategory = (
  countryOptionBuilder: () => { value: string; label: string }[]
): FormCategory => {
  const category: FormCategory = {
    categoryName: 'address',
    fields: [
      {
        objectKey: 'adrResStreet',
        langKey: 'address'
      },
      {
        objectKey: 'adrResPostalCode',
        langKey: 'postalCode'
      },
      {
        objectKey: 'adrResCity',
        langKey: 'city'
      },
      {
        objectKey: 'adrResCountryCode',
        langKey: 'country',
        fieldType: 'select',
        optionsBuilder: countryOptionBuilder
      }
    ]
  };

  return category;
};

const getAdditionalInfoCategory = (
  showEmployeeRemarks: boolean,
  showAgencyRemarks: boolean,
  showIssues: boolean
): FormCategory => {
  const category: FormCategory = {
    categoryName: 'additionalInfo',
    fields: [
      ...(showEmployeeRemarks
        ? ([
            {
              objectKey: 'remarks',
              langKey: 'remarksEmployee',
              fieldType: 'textarea'
            }
          ] as Array<FormField>)
        : []),
      ...(showAgencyRemarks
        ? ([
            {
              objectKey: 'agenciesRemarks',
              langKey: 'remarksAgency',
              fieldType: 'textarea'
            }
          ] as Array<FormField>)
        : [])
    ]
  };

  if (showIssues) {
    category.fields.push({
      objectKey: 'issues',
      fieldType: 'textarea'
    });
  }

  return category;
};
